/**
 * Media Picker — three tabs: AI 生成 / WordPress 媒体库 / 本地上传。
 */
import {useEffect, useState} from 'react';
import {mediaAssetsApi, siteContentApi, type SiteMediaItem} from '../../lib/api-resources';
import type {
  EditorMediaRef,
  SiteMediaAsset,
  SiteMediaRole,
} from '../../lib/types';
import {friendlyError} from '../../lib/error-labels';

type Tab = 'upload' | 'wp' | 'ai';

interface Props {
  siteId: string;
  role: SiteMediaRole;
  pageId?: string | null;
  sectionId?: string | null;
  onSelect: (m: EditorMediaRef) => void;
  onClose: () => void;
  title?: string;
  initialAttachmentId?: number | null;
}

export default function MediaPicker({
  siteId, role, pageId, sectionId, onSelect, onClose,
  title = '选择或生成图片', initialAttachmentId,
}: Props) {
  const [tab, setTab] = useState<Tab>('ai');
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal media-picker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="icon-btn" onClick={onClose} title="关闭">×</button>
        </div>
        <div className="media-picker-tabs">
          <button
            className={`tab ${tab === 'ai' ? 'active' : ''}`}
            onClick={() => setTab('ai')}
          >
            ✨ AI 生成图片
          </button>
          <button
            className={`tab ${tab === 'wp' ? 'active' : ''}`}
            onClick={() => setTab('wp')}
          >
            📚 WordPress 媒体库
          </button>
          <button
            className={`tab ${tab === 'upload' ? 'active' : ''}`}
            onClick={() => setTab('upload')}
          >
            ⬆ 本地上传
          </button>
        </div>
        {tab === 'ai' && (
          <GenerateAiTab
            siteId={siteId} role={role} pageId={pageId} sectionId={sectionId}
            onSelect={onSelect} onClose={onClose}
          />
        )}
        {tab === 'wp' && (
          <WpMediaTab
            siteId={siteId} onSelect={onSelect} onClose={onClose}
            initialAttachmentId={initialAttachmentId}
          />
        )}
        {tab === 'upload' && (
          <UploadTab
            siteId={siteId} onSelect={onSelect} onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

function GenerateAiTab({siteId, role, pageId, sectionId, onSelect, onClose}: {
  siteId: string;
  role: SiteMediaRole;
  pageId?: string | null;
  sectionId?: string | null;
  onSelect: (m: EditorMediaRef) => void;
  onClose: () => void;
}) {
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [candidates, setCandidates] = useState<SiteMediaAsset[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<SiteMediaAsset | null>(null);
  const [altText, setAltText] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);

  const preferredCandidate = (items: SiteMediaAsset[]) =>
    items.find((c) => c.generation_status === 'READY' && c.publish_status === 'PUBLISH_FAILED')
    ?? items.find((c) => c.generation_status === 'READY')
    ?? items[0]
    ?? null;

  const loadExisting = async () => {
    setLoadingExisting(true);
    setError(null);
    try {
      const r = await mediaAssetsApi.list(siteId, {
        role,
        page_id: pageId ?? null,
        section_id: sectionId ?? null,
      });
      setCandidates(r.items);
      setSelectedAsset(preferredCandidate(r.items));
    } catch (e: any) {
      const f = friendlyError(e?.code, e?.message);
      setError(f.headline);
    } finally {
      setLoadingExisting(false);
    }
  };

  useEffect(() => {
    void loadExisting();
  }, [siteId, role, pageId, sectionId]);

  const onGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setError(null);
    setCandidates([]);
    setSelectedAsset(null);
    try {
      await mediaAssetsApi.generate(siteId, {
        role,
        page_id: pageId ?? null,
        section_id: sectionId ?? null,
        prompt: prompt.trim(),
        wait: true,
      });
      const r = await mediaAssetsApi.list(siteId, {
        role,
        page_id: pageId ?? null,
        section_id: sectionId ?? null,
      });
      setCandidates(r.items);
      setSelectedAsset(preferredCandidate(r.items));
    } catch (e: any) {
      const f = friendlyError(e?.code, e?.message);
      setError(f.headline);
    } finally {
      setGenerating(false);
    }
  };

  const onConfirmSelect = async () => {
    if (!selectedAsset || selectedAsset.generation_status !== 'READY') return;
    try {
      await mediaAssetsApi.select(siteId, selectedAsset.id, {
        alt_text: altText || selectedAsset.alt_text || null,
      });
      const attachmentId = selectedAsset.wp_attachment_id;
      if (attachmentId) {
        onSelect({attachment_id: attachmentId, alt: altText || selectedAsset.alt_text || null});
      } else {
        onSelect({attachment_id: -1, alt: altText || selectedAsset.alt_text || null});
      }
      onClose();
    } catch (e: any) {
      const f = friendlyError(e?.code, e?.message);
      setError(f.headline);
    }
  };

  const onPublishSelected = async () => {
    if (
      !selectedAsset
      || selectedAsset.generation_status !== 'READY'
      || selectedAsset.publish_status === 'PUBLISHED'
    ) return;
    setPublishing(true);
    setError(null);
    try {
      const r = await mediaAssetsApi.publish(siteId, selectedAsset.id);
      onSelect({
        attachment_id: r.wp_attachment_id,
        alt: altText || r.asset.alt_text || null,
      });
      onClose();
    } catch (e: any) {
      const f = friendlyError(e?.code, e?.message);
      setError(f.headline);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="media-picker-tab-content">
      {error && <div className="editor-error">{error}</div>}

      {loadingExisting && <p className="empty-hint">正在加载已有候选图…</p>}

      {!loadingExisting && !candidates.length && (
        <div className="ai-generate-form">
          <label className="field">
            <span className="field-label">图片描述（输入你想要的效果）</span>
            <textarea
              rows={4}
              placeholder="例如：工业水泵特写，金属表面，柔和的自然光，浅色背景"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
          </label>
          <button
            className="btn-primary"
            disabled={generating || !prompt.trim()}
            onClick={onGenerate}
          >
            {generating ? '生成中（最长约 3 分钟）…' : '✨ 生成 3 张候选'}
          </button>
          <p className="empty-hint">
            每次会生成 3 张候选图供选择。生成过程由图片服务完成，可能需要几十秒到几分钟。
          </p>
        </div>
      )}

      {!loadingExisting && candidates.length > 0 && (
        <div className="ai-candidates">
          <p className="empty-hint">已有候选图已自动载入。生成状态与 WordPress 发布状态分开显示；发布重试不会重新生成图片。</p>
          <div className="ai-candidates-grid">
            {candidates.map((c) => (
              <div
                key={c.id}
                className={`ai-candidate ${selectedAsset?.id === c.id ? 'selected' : ''}`}
                onClick={() => setSelectedAsset(c)}
              >
                {c.preview_url ? (
                  <img src={c.preview_url} alt={c.alt_text || 'AI 候选图'} />
                ) : (
                  <div className="ai-candidate-placeholder">（无预览）</div>
                )}
                <div className="ai-candidate-meta">
                  <small>{c.provider || 'AI'}</small>
                  <small>{mediaLifecycleLabel(c)}</small>
                  {c.publish_error_message && c.publish_status === 'PUBLISH_FAILED' && (
                    <small className="error-text">{c.publish_error_message}</small>
                  )}
                  {c.generation_error_message && c.generation_status !== 'READY' && (
                    <small className="error-text">{c.generation_error_message}</small>
                  )}
                </div>
              </div>
            ))}
          </div>
          <label className="field">
            <span className="field-label">替代文字（无障碍 / SEO 用途）</span>
            <input
              type="text"
              placeholder="例如：不锈钢离心泵白底产品图"
              value={altText}
              onChange={(e) => setAltText(e.target.value)}
            />
          </label>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={onClose}>取消</button>
            <button
              className="btn-secondary"
              onClick={onConfirmSelect}
              disabled={!selectedAsset || selectedAsset.generation_status !== 'READY'}
            >
              使用此图（仅预览）
            </button>
            {selectedAsset?.source === 'IMAGE_SERVICE'
              && selectedAsset.generation_status === 'READY'
              && selectedAsset.publish_status !== 'PUBLISHED' && (
              <button
                className="btn-primary"
                onClick={onPublishSelected}
                disabled={publishing || !selectedAsset || selectedAsset.publish_status === 'PUBLISHING'}
              >
                {publishing
                  ? '上传中…'
                  : selectedAsset.publish_status === 'PUBLISH_FAILED'
                    ? '重新发布到 WordPress'
                    : '发布到 WordPress'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function mediaLifecycleLabel(asset: SiteMediaAsset): string {
  if (asset.generation_status === 'READY' && asset.publish_status === 'PUBLISH_FAILED') {
    return '图片已生成，发布失败';
  }
  if (asset.generation_status === 'READY' && asset.publish_status === 'PUBLISHED') {
    return '已发布到 WordPress';
  }
  if (asset.generation_status === 'READY') return '图片已生成';
  if (asset.generation_status === 'GENERATING') return '生成中';
  if (asset.generation_status === 'STALE') return '生成任务已过期';
  if (asset.generation_status === 'GENERATION_FAILED') return '图片生成失败';
  return asset.status;
}

function WpMediaTab({siteId, onSelect, onClose, initialAttachmentId}: {
  siteId: string;
  onSelect: (m: EditorMediaRef) => void;
  onClose: () => void;
  initialAttachmentId?: number | null;
}) {
  const [items, setItems] = useState<SiteMediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(initialAttachmentId ?? null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await siteContentApi.listMedia(siteId, {per_page: 30, media_type: 'image'});
      setItems(r.items);
    } catch (e: any) {
      const f = friendlyError(e?.code, e?.message);
      setError(f.headline);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [siteId]);

  const confirm = () => {
    if (selectedId == null) return;
    const item = items.find((i) => i.attachment_id === selectedId);
    onSelect({attachment_id: selectedId, alt: item?.alt || null});
  };

  return (
    <div className="media-picker-tab-content">
      {error && <div className="editor-error">{error}</div>}
      <div className="media-picker-list">
        {loading && <p>加载中…</p>}
        {!loading && items.length === 0 && (
          <p className="empty-hint">媒体库还没有图片。可以切换到「本地上传」标签上传一张。</p>
        )}
        {items.map((it) => (
          <div
            key={it.attachment_id}
            className={`media-picker-item ${selectedId === it.attachment_id ? 'selected' : ''}`}
            onClick={() => setSelectedId(it.attachment_id)}
          >
            <img src={it.url} alt={it.alt || it.title} />
            <div className="media-picker-item-meta">
              <strong>{it.title || '（无标题）'}</strong>
              <small>#{it.attachment_id} · {it.mime_type}</small>
            </div>
          </div>
        ))}
      </div>
      <div className="modal-footer">
        <button className="btn-secondary" onClick={onClose}>取消</button>
        <button className="btn-primary" onClick={confirm} disabled={selectedId == null}>
          选中
        </button>
      </div>
    </div>
  );
}

function UploadTab({siteId, onSelect, onClose}: {
  siteId: string;
  onSelect: (m: EditorMediaRef) => void;
  onClose: () => void;
}) {
  const [alt, setAlt] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedId, setUploadedId] = useState<number | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);

  const onUpload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const r = await siteContentApi.uploadMedia(siteId, file, alt || undefined);
      setUploadedId(r.attachment_id);
      setUploadedUrl(r.url);
    } catch (e: any) {
      const f = friendlyError(e?.code, e?.message);
      setError(f.headline);
    } finally {
      setUploading(false);
    }
  };

  const confirm = () => {
    if (uploadedId == null) return;
    onSelect({attachment_id: uploadedId, alt: alt || null});
  };

  return (
    <div className="media-picker-tab-content">
      {error && <div className="editor-error">{error}</div>}
      <div className="media-picker-upload">
        <label className="field">
          <span className="field-label">替代文字（可选）</span>
          <input
            type="text"
            placeholder="用于无障碍访问和搜索引擎识别图片内容"
            value={alt}
            onChange={(e) => setAlt(e.target.value)}
          />
        </label>
        <label className="btn-secondary file-input-label">
          {uploading ? '上传中…' : '+ 选择文件'}
          <input
            type="file"
            accept="image/*"
            style={{display: 'none'}}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                onUpload(e.target.files[0]);
              }
            }}
          />
        </label>
        {uploadedUrl && (
          <div className="upload-preview">
            <img src={uploadedUrl} alt={alt || '已上传图片'} />
            <small>已上传 #{uploadedId}</small>
          </div>
        )}
      </div>
      <div className="modal-footer">
        <button className="btn-secondary" onClick={onClose}>取消</button>
        <button className="btn-primary" onClick={confirm} disabled={uploadedId == null}>
          选中
        </button>
      </div>
    </div>
  );
}
